import { useContext, useEffect, useState, useMemo, useCallback } from 'react'
import styled from '@emotion/styled'
import { t } from '@lingui/core/macro'
import AddIcon from '@mui/icons-material/Add'
import Collapse from '@mui/material/Collapse'
import Fab from '@mui/material/Fab'
import Typography from '@mui/material/Typography'
import { useSelector, useDispatch } from 'react-redux'
import uuidv4 from 'uuid/v4'
import { Paper } from '@components'
import { integrationContext, connectorContext } from '@contexts'
import { api } from 'api'
import { IntegrationExpansionPanel } from './integration-expansion-panel'
import { getIntegrationFormat } from './integrationFormatUtil'
import { IntegrationPicker } from './new-integration-picker'
import { useGetType } from './use-get-type'
import { setIntegrations, setIntegrationsLoaded } from '../template.actions'
import { templateCopyContext } from './context'

const Scrollable = styled.div`
  overflow-y: scroll;
  overflow-x: hidden;
  height: calc(100% - 80px);
  width: 100%;
`

const Container = styled.div`
  padding: 30px;
`

const CenterTypography = styled(Typography)`
  text-align: center;
`

const AddIntegration = styled(Fab)`
  margin-left: 24px;
`

export const Integrate = () => {
  const template = useSelector(state => state.Template)
  const { fetchIntegrations, inboundById, outboundById } = useContext(integrationContext)
  const { fetchConnectors } = useContext(connectorContext)
  const accountId = useSelector(state => state.Auth.account.id)
  const dispatch = useDispatch()

  const [openIn, setOpenIn] = useState()
  const [openOut, setOpenOut] = useState()
  const [addOut, setAddOut] = useState(false)
  const [addIn, setAddIn] = useState(false)

  /**
   * This function is used to load the integrations into the template
   * without setting the `hasUserChangedData` flag to true.
   * This is meant to load the integrations data when the template is loaded
   * and not when the user has made changes to the integrations.
   */
  const onLoadIntegration = useCallback(
    newState =>
      typeof newState === 'function'
        ? dispatch(setIntegrationsLoaded(newState(template.integrationsById)))
        : dispatch(setIntegrationsLoaded(newState)),
    [dispatch, template.integrationsById],
  )

  const onChange = useCallback(
    newState =>
      typeof newState === 'function'
        ? dispatch(setIntegrations(newState(template.integrationsById)))
        : dispatch(setIntegrations(newState)),
    [dispatch, template.integrationsById],
  )

  useEffect(() => {
    fetchIntegrations()
  }, [fetchIntegrations])

  useEffect(() => {
    fetchConnectors()
  }, [fetchConnectors])

  const removeIntegration = useCallback(
    id => {
      api.post('/api/v1/integration-assignments/bulk/delete', { ids: [id] })
      onChange(template.integrations.filter(integration => id !== integration.id))
    },
    [onChange, template],
  )

  const removeIntegrations = useCallback(
    ids => {
      api.post('/api/v1/integration-assignments/bulk/delete', { ids })
      onChange(template.integrations.filter(integration => !ids.find(i => i == integration.id)))
    },
    [onChange, template],
  )

  const output = useGetType(template ? Object.values(template.integrationsById) : {}, 'OUTPUT')
    .concat(useGetType(template ? Object.values(template.integrationsById) : {}, 'SUPPLEMENTAL'))
    .map(integration => (
      <Paper key={integration.id} elevaton={2} style={{ margin: 2 }}>
        <IntegrationExpansionPanel
          open={openOut === integration.id}
          onDelete={removeIntegration}
          onMultiDelete={removeIntegrations}
          selected=""
          onSelect={() => setOpenOut(prev => (prev === integration.id ? null : integration.id))}
          key={integration.id}
          assignment={integration}
        />
      </Paper>
    ))

  const input = useGetType(template ? Object.values(template.integrationsById) : {}, 'INPUT').map(
    integration => (
      <Paper key={integration.id} elevaton={2} style={{ margin: 2 }}>
        <IntegrationExpansionPanel
          open={openIn === integration.id}
          onDelete={removeIntegration}
          onMultiDelete={removeIntegrations}
          selected=""
          onSelect={() => setOpenIn(prev => (prev === integration.id ? null : integration.id))}
          type="input"
          key={integration.id}
          assignment={integration}
        />
      </Paper>
    ),
  )

  const addIntegration = useCallback(
    integrationId => {
      const tempId = `TEMP_${uuidv4()}`
      const integration = inboundById[integrationId]
        ? inboundById[integrationId]
        : outboundById[integrationId]

      onChange([
        ...template.integrations,
        {
          ...getIntegrationFormat(integration, template),
          id: tempId,
        },
      ])

      if (integration.type === 'INPUT') setOpenIn(tempId)
      else if (integration.type === 'OUTPUT') setOpenOut(tempId)

      return tempId
    },
    [accountId, template, inboundById, outboundById, onChange, setOpenIn, setOpenOut],
  )

  const value = useMemo(
    () => ({
      onChange,
      onLoadIntegration,
      template,
    }),
    [template, onChange, onLoadIntegration],
  )

  return (
    <templateCopyContext.Provider value={value}>
      <Container id="integrations">
        <Scrollable>
          <div style={{ marginTop: 24, marginBottom: 48 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, height: 45 }}>
              <Typography variant="h4">{t`Inbound`}</Typography>
              <div>
                <AddIntegration
                  color="secondary"
                  size="small"
                  onClick={() => setAddIn(!addIn)}
                  data-testid="add-inbound-integration"
                >
                  <AddIcon
                    style={{
                      transform: addIn ? 'rotate(45deg' : '',
                      transition: 'all 150ms ease-out',
                    }}
                  />
                </AddIntegration>
              </div>
            </div>
            <Collapse in={addIn}>
              <IntegrationPicker
                selected={-1}
                onSelect={e => {
                  setAddIn(false)
                  addIntegration(e())
                }}
                type="input"
                existingIntegrations={template.integrations || []}
              />
            </Collapse>
            {input.length ? (
              input
            ) : (
              <CenterTypography variant="body2">{t`No active integrations`}</CenterTypography>
            )}
          </div>
          <div style={{ borderBottom: '1px dashed #ccc' }} />
          <div style={{ marginTop: 48 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, height: 45 }}>
              <Typography variant="h4">{t`Outbound`}</Typography>
              <div>
                <AddIntegration
                  color="secondary"
                  size="small"
                  onClick={() => setAddOut(!addOut)}
                  data-testid="add-outbound-integration"
                >
                  <AddIcon
                    style={{
                      transform: addOut ? 'rotate(45deg' : '',
                      transition: 'all 150ms ease-out',
                    }}
                  />
                </AddIntegration>
              </div>
            </div>
            <Collapse in={addOut}>
              <IntegrationPicker
                selected={-1}
                onSelect={e => {
                  setAddOut(false)
                  addIntegration(e())
                }}
                existingIntegrations={template.integrations || []}
              />
            </Collapse>
            {output.length ? (
              output
            ) : (
              <CenterTypography variant="body2">{t`No active integrations`}</CenterTypography>
            )}
          </div>
        </Scrollable>
      </Container>
    </templateCopyContext.Provider>
  )
}
